import axios from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { Palette, chartPalette, datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

type Region = 'BR' | 'N' | 'S' | 'SE' | 'CO' | 'NE'

export const RAIZEN_12M_COLOR = '#8B008B'
export const RAIZEN_3M_COLOR = '#D099D0'
export const RAIZEN_COLOR = '#8B008B'
export const RAIZEN_COUNT_COLOR = '#D099D0'
const DISTRIBUTION_CHARTS_PATH = '/api/charts/distribution'

export namespace Distribution {
  export namespace Importacao {
    const CONTEXT_ID = 'equity:distribution:importacao'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'volume_gasolina'
        | 'volume_diesel'
        | 'volume_etanol'
        | 'paridade_gasolina'
        | 'paridade_diesel'
        | 'indicadores_macro'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'indicadores_macro':
            return getIndicadoresMacroChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const isPercentChart = (chart: string) =>
      chart === 'paridade_gasolina' || chart === 'paridade_diesel'

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(isPercentChart(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            split: false,
            shared: true,
            xDateFormat: isPercentChart(config.chart)
              ? '%A, %b %e, %Y'
              : '%B %Y',
          },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    const getIndicadoresMacroChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: { xDateFormat: '%A, %b %e, %Y' },
          yAxis: [{ id: 'primary' }, { id: 'secondary', opposite: true }],
        },
        {
          type: Array(data.data.length).fill('spline'),
          yAxis: data.data.map((s) =>
            s.name.includes('usdbrl') ? 'secondary' : 'primary'
          ),
        }
      )(config, data)
  }

  export namespace EtanolHidratado {
    const CONTEXT_ID = 'equity:distribution:etanol_hidratado'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'paridade_bomba' | 'share_etanol' | 'mix_etanol'
      selectedOptions?: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        config.chart === 'paridade_bomba'
          ? getParidadeBombaChartProps(config, data)
          : getEtanolHidratadoControlledChartProps(config, data),
    }

    const getParidadeBombaChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleColumnChartProps(
        Context,
        { addMinMaxIndicators: true },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    const getEtanolHidratadoControlledChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const isSelected = (series: string) =>
        config.selectedOptions?.includes(series)

      const series = data.data
        ?.filter((s) => isSelected(s.name))
        .map((s, idx) => ({
          data: convertCompassToHighchartsDataFormat(s.data, {
            convertPercentage: true,
          }),
          id: `series-${idx}`,
          name: s.name,
          type: 'line',
          tooltip: {
            valueDecimals: 2,
            valueSuffix: '%',
            xDateFormat: '%B %Y',
          },
          yAxis: 'default',
        }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { zoomType: 'x' },
            colors: chartPalette,
            series: series as any,
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace AdicaoEChurn {
    const CONTEXT_ID = 'equity:distribution:adicao_e_churn'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'adicao_bruta'
        | 'adicao_anualizada'
        | 'churn_bruto'
        | 'churn_anualizado'
        | 'liquido'
        | 'liquido_anualizado'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getNetFundingChartProps(config, data),
    }

    const getNetFundingChartProps = (config: ChartConfig, data: ChartData) => {
      const hasPercentualSecondaryYAxis = (chart: string) =>
        chart === 'adicao_anualizada' ||
        chart === 'churn_anualizado' ||
        chart === 'liquido_anualizado'
      return getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(
            hasPercentualSecondaryYAxis(config.chart)
          ),
          legend: { maxHeight: 80 },
          tooltip: {
            shared: true,
            split: false,
            pointFormatter: magnitudePointFormatter({
              unit: hasPercentualSecondaryYAxis(config.chart) ? '%' : '',
            }),
            xDateFormat: '%m/%Y',
          },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
          yAxis: [
            {
              id: 'primary',
              title: { text: '' },
              ...(hasPercentualSecondaryYAxis(config.chart)
                ? { labelSuffix: '%' }
                : {}),
            },
            {
              id: 'secondary',
              title: { text: '' },
              opposite: true,
              ...(hasPercentualSecondaryYAxis(config.chart)
                ? { labelSuffix: '%' }
                : {}),
            },
          ],
        },
        {
          color: data.data.map((s) =>
            s.name === 'PETROBRAS DISTRIBUIDORA S.A. - 12m'
              ? Palette.darkGreen
              : s.name === 'PETROBRAS DISTRIBUIDORA S.A. - 3m'
              ? Palette.fadedGreen
              : s.name === 'IPIRANGA - 12m'
              ? Palette.darkYellow
              : s.name === 'IPIRANGA - 3m'
              ? Palette.fadedYellow
              : s.name === 'RAIZEN - 12m'
              ? RAIZEN_12M_COLOR
              : s.name === 'RAIZEN - 3m'
              ? RAIZEN_3M_COLOR
              : s.name === 'BANDEIRA BRANCA - 12m'
              ? Palette.darkGray
              : Palette.gray
          ),
          type: data.data.map((series) =>
            series.name.includes('3m') ? 'column' : 'spline'
          ),
          yAxis: data.data.map((series) =>
            series.name.includes('3m') ? 'primary' : 'secondary'
          ),
        }
      )(config, data)
    }
  }

  export namespace BandeiraBranca {
    const CONTEXT_ID = 'equity:distribution:bandeira_branca'

    type BandeiraBrancaChartId =
      | 'venda_gasolina'
      | 'venda_diesel'
      | 'venda_etanol'
      | 'share_gasolina'
      | 'share_diesel'
      | 'share_etanol'

    const BANDEIRA_BRANCA_CHART_PATHS: Record<BandeiraBrancaChartId, string> = {
      venda_gasolina: '/bandeira-branca/venda-gasolina',
      venda_diesel: '/bandeira-branca/venda-diesel',
      venda_etanol: '/bandeira-branca/venda-etanol',
      share_gasolina: '/bandeira-branca/share-gasolina',
      share_diesel: '/bandeira-branca/share-diesel',
      share_etanol: '/bandeira-branca/share-etanol',
    }

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart: BandeiraBrancaChartId
      title: string
      distributors: string[]
      region: Region
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          DISTRIBUTION_CHARTS_PATH + BANDEIRA_BRANCA_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          distributors: config.distributors,
          region: config.region,
        },
      }).then((response) => response.data)

    const isShareChart = (chart: BandeiraBrancaChartId) =>
      chart === 'share_gasolina' ||
      chart === 'share_diesel' ||
      chart === 'share_etanol'

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        color: s.name.includes('PETROBRAS')
          ? Palette.darkGreen
          : s.name.includes('IPIRANGA')
          ? Palette.darkYellow
          : s.name.includes('RAIZEN')
          ? RAIZEN_COLOR
          : Palette.gray,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: isShareChart(config.chart),
        }),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: {
          valueDecimals: 2,
          xDateFormat: '%A, %b %e, %Y',
          ...(isShareChart(config.chart) ? { valueSuffix: '%' } : {}),
        },
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          ...(isShareChart(config.chart)
            ? { labels: { format: '{value}%' } }
            : {}),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Demanda {
    const CONTEXT_ID = 'equity:distribution:demanda'

    type DemandaChartId =
      | 'diesel'
      | 'diesel_yoy'
      | 'ciclo_otto'
      | 'ciclo_otto_yoy'
      | 'gasolina'
      | 'gasolina_yoy'
      | 'etanol'
      | 'etanol_yoy'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart: DemandaChartId
      title: string
      regions: Region[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const DEMANDA_CHART_PATHS: Record<DemandaChartId, string> = {
      diesel: '/demanda/diesel',
      diesel_yoy: '/demanda/diesel-yoy',
      ciclo_otto: '/demanda/ciclo-otto',
      ciclo_otto_yoy: '/demanda/ciclo-otto-yoy',
      gasolina: '/demanda/gasolina',
      gasolina_yoy: '/demanda/gasolina-yoy',
      etanol: '/demanda/etanol',
      etanol_yoy: '/demanda/etanol-yoy',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: DISTRIBUTION_CHARTS_PATH + DEMANDA_CHART_PATHS[config.chart],
        method: 'GET',
      }).then((response) => response.data)

    const isYoyChart = (chart: DemandaChartId) =>
      chart === 'diesel_yoy' ||
      chart === 'ciclo_otto_yoy' ||
      chart === 'gasolina_yoy' ||
      chart === 'etanol_yoy'

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isSelected = (series: Region) =>
        config.regions.includes(series) ||
        config.regions.map((region) => region + ' 3M').includes(series)

      const series = data.data
        ?.filter((s) => isSelected(s.name as Region))
        .map((s, idx) => ({
          color:
            s.name === 'BR'
              ? Palette.darkBlue
              : s.name === 'BR 3M'
              ? Palette.navyBlue
              : s.name === 'SE'
              ? Palette.darkGreen
              : s.name === 'SE 3M'
              ? Palette.green
              : s.name === 'S'
              ? Palette.darkGray
              : s.name === 'S 3M'
              ? Palette.gray
              : s.name === 'N'
              ? Palette.darkYellow
              : s.name === 'N 3M'
              ? Palette.fadedYellow
              : s.name === 'NE'
              ? Palette.darkBlue
              : s.name === 'NE 3M'
              ? Palette.navyBlue
              : s.name === 'CO'
              ? Palette.darkGreen
              : Palette.green,
          data: convertCompassToHighchartsDataFormat(s.data, {
            convertPercentage: isYoyChart(config.chart),
          }),
          id: `series-${idx}`,
          marker: { enabled: false },
          name: s.name,
          type: 'spline',
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: isYoyChart(config.chart) ? '%' : '',
            }),
            valueDecimals: 2,
            xDateFormat: '%B %Y',
            ...(isYoyChart(config.chart) ? { valueSuffix: '%' } : {}),
          },
          yAxis: 'default',
        }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: isYoyChart(config.chart) ? '' : 'm3/mês' },
          ...(isYoyChart(config.chart)
            ? { labels: { format: '{value}%' } }
            : {}),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
            colors: chartPalette,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace FuelImport {
    const CONTEXT_ID = 'equity:distribution:fuel_import'

    type FuelImportChartId = 'gasolina' | 'diesel'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart: FuelImportChartId
      title: string
      region: Region
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const FUEL_IMPORT_CHART_PATHS: Record<FuelImportChartId, string> = {
      gasolina: '/importacao/fuel-import-gasolina',
      diesel: '/importacao/fuel-import-diesel',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: DISTRIBUTION_CHARTS_PATH + FUEL_IMPORT_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          region: config.region,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        color: s.name.includes('PETROBRAS')
          ? Palette.darkGreen
          : s.name.includes('IPIRANGA')
          ? Palette.darkYellow
          : s.name.includes('RAIZEN')
          ? RAIZEN_COLOR
          : Palette.gray,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: {
          valueDecimals: 2,
          valueSuffix: '%',
          xDateFormat: '%B %Y',
        },
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
            colors: chartPalette,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Precos {
    const CONTEXT_ID = 'equity:distribution:precos'

    type PrecosChartId =
      | 'sell_in_branca'
      | 'sell_out_branca'
      | 'sell_in'
      | 'sell_out'
      | 'spread'
      | 'refinaria'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart: PrecosChartId
      title: string
      regiao?: Region
      estado?: string
      municipio?: string
      produto?: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const PRECOS_CHART_PATHS: Record<PrecosChartId, string> = {
      sell_in_branca: '/precos/sell-in-branca',
      sell_out_branca: '/precos/sell-out-branca',
      sell_in: '/precos/sell-in',
      sell_out: '/precos/sell-out',
      spread: '/precos/spread',
      refinaria: '/precos/refinaria',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: DISTRIBUTION_CHARTS_PATH + PRECOS_CHART_PATHS[config.chart],
        ...(config.chart === 'refinaria'
          ? { method: 'GET' }
          : {
              method: 'POST',
              data: {
                regiao: config.regiao,
                estado: config.estado,
                municipio: config.municipio,
                produto: config.produto,
              },
            }),
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data
        ? data.data.map((s, idx) => ({
            color:
              s.name === 'BR'
                ? Palette.darkGreen
                : s.name === 'BR count'
                ? Palette.fadedGreen
                : s.name === 'IPIRANGA'
                ? Palette.darkYellow
                : s.name === 'IPIRANGA count'
                ? Palette.fadedYellow
                : s.name === 'RAIZEN'
                ? RAIZEN_COLOR
                : s.name === 'RAIZEN count'
                ? RAIZEN_COUNT_COLOR
                : s.name === 'OTHERS'
                ? Palette.darkGray
                : Palette.gray,
            data: convertCompassToHighchartsDataFormat(s.data),
            id: `series-${idx}`,
            marker: { enabled: false },
            name: s.name,
            type: s.name.includes('count') ? 'column' : 'spline',
            tooltip: { valueDecimals: 2, xDateFormat: '%m/%Y' },
            yAxis: s.name.includes('count') ? 'secondary' : 'default',
          }))
        : []

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          height: '60%',
          title: { text: '' },
        },
        {
          id: 'secondary',
          opposite: false,
          height: '35%',
          top: '65%',
          offset: 0,
          title: { text: '' },
          max: 100,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: true, split: false },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'refinaria':
            return getMultipleLineChartProps(
              Context,
              { tooltip: { xDateFormat: '%B %Y' } },
              { type: Array(data.data.length).fill('spline') }
            )(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }
  }

  export namespace Share {
    const CONTEXT_ID = 'equity:distribution:share'

    type ShareChartId =
      | 'gasolina'
      | 'gasolina_mom'
      | 'diesel'
      | 'diesel_mom'
      | 'etanol'
      | 'etanol_mom'
      | 'otto'
      | 'otto_mom'
      | 'diesel_revenda'
      | 'diesel_revenda_mom'
      | 'diesel_ex_rev'
      | 'diesel_ex_rev_mom'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart: ShareChartId
      title: string
      region: Region
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const SHARE_CHART_PATHS: Record<ShareChartId, string> = {
      gasolina: '/share/gasolina',
      gasolina_mom: '/share/gasolina-mom',
      diesel: '/share/diesel',
      diesel_mom: '/share/diesel-mom',
      etanol: '/share/etanol',
      etanol_mom: '/share/etanol-mom',
      otto: '/share/otto',
      otto_mom: '/share/otto-mom',
      diesel_revenda: '/share/diesel-revenda',
      diesel_revenda_mom: '/share/diesel-revenda-mom',
      diesel_ex_rev: '/share/diesel-ex-rev',
      diesel_ex_rev_mom: '/share/diesel-ex-rev-mom',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: DISTRIBUTION_CHARTS_PATH + SHARE_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          region: config.region,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        color:
          s.name === 'PETROBRAS DISTRIBUIDORA S.A.'
            ? Palette.darkGreen
            : s.name === 'PETROBRAS DISTRIBUIDORA S.A. - 3m'
            ? Palette.fadedGreen
            : s.name === 'IPIRANGA PRODUTOS DE PETRÓLEO S.A'
            ? Palette.darkYellow
            : s.name === 'IPIRANGA PRODUTOS DE PETRÓLEO S.A - 3m'
            ? Palette.fadedYellow
            : s.name === 'RAIZEN COMBUSTÍVEIS S.A.'
            ? RAIZEN_COLOR
            : s.name === 'RAIZEN COMBUSTÍVEIS S.A. - 3m'
            ? RAIZEN_COUNT_COLOR
            : s.name === 'OTHERS'
            ? Palette.darkGray
            : s.name === 'OTHERS - 3m'
            ? Palette.gray
            : '',
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: {
          valueDecimals: 2,
          valueSuffix: '%',
          xDateFormat: '%B %Y',
        },
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:distribution:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ev_ebitda_ratio_vbbr3_ugpa3'
        | 'pe_ratio_vbbr3_ugpa3'
        | 'ev_ebitda_ugpa3'
        | 'ev_ebitda_vbbr3'
        | 'pe_ugpa3'
        | 'pe_vbbr3'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ev_ebitda_ratio_vbbr3_ugpa3':
            return getRatioChartProps(
              config,
              data,
              'Ratio EV/EBITDA F12m (BR/Ultra)',
              ['Ratio', 'Vibra', 'Ultra']
            )
          case 'pe_ratio_vbbr3_ugpa3':
            return getRatioChartProps(
              config,
              data,
              'Ratio PE F12m (Vibra/Ultra)',
              ['Ratio', 'Vibra', 'Ultra']
            )
          case 'ev_ebitda_ugpa3':
            return getMultipleLineChartPropsWrapper(
              config,
              data,
              'EV/EBITDA F12m Ultra'
            )
          case 'ev_ebitda_vbbr3':
            return getMultipleLineChartPropsWrapper(
              config,
              data,
              'EV/EBITDA F12m Vibra'
            )
          case 'pe_ugpa3':
            return getMultipleLineChartPropsWrapper(
              config,
              data,
              'PE F12m Ultra'
            )
          case 'pe_vbbr3':
            return getMultipleLineChartPropsWrapper(
              config,
              data,
              'PE F12m Vibra'
            )
        }
      },
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string,
      seriesNames: string[] = ['']
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: seriesNames }
      )(config, data)

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string,
      seriesNames: string[]
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicatorsSeriesIndexes: [0],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        {
          name: seriesNames,
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)
  }
}
